
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';

export default function ProjectsSection() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      title: t('githubTitle'),
      description: t('githubDesc'),
      icon: "ri-github-fill",
      link: "https://github.com/inteliker",
      stats: t('githubStats'),
      tech: ["Python", "OSINT", "Threat Intel", "Security Tools"]
    },
    {
      title: t('twitterTitle'),
      description: t('twitterDesc'),
      icon: "ri-twitter-x-fill",
      link: "https://x.com/inteliker",
      stats: t('twitterStats'),
      tech: ["CTI", "Threat Hunting", "IOCs", "Security News"]
    }
  ];

  return (
    <section ref={sectionRef} id="projects" className="py-20 bg-black relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2306b6d4' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Section header */}
          <div className="text-center mb-16">
            <div className="font-mono text-cyan-400 text-sm mb-4">
              <span className="text-gray-500">root@cyberintel:~$</span> {t('projectsTerminal')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-mono">
              <span className="text-cyan-400">&gt;</span> {t('projectsTitle')}
            </h2>
            <p className="text-gray-400 font-mono text-sm max-w-2xl mx-auto">
              {t('projectsSubtitle')}
            </p>
          </div>

          {/* Projects grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {projects.map((project, index) => (
              <div 
                key={index}
                className={`group bg-gray-900/50 border border-gray-700 hover:border-cyan-500/50 rounded-lg overflow-hidden transition-all duration-500 hover:bg-gray-800/50 hover:shadow-xl hover:shadow-cyan-500/10 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="p-8">
                  {/* Project header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-black/50 border border-gray-600 rounded-lg group-hover:border-cyan-500/50 transition-colors duration-300">
                      <i className={`${project.icon} text-2xl text-cyan-400`}></i>
                    </div>
                    <div className="text-xs font-mono text-gray-500 bg-gray-800/50 px-3 py-1 rounded-full border border-gray-700">
                      {project.stats}
                    </div>
                  </div>

                  {/* Project content */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-white mb-3 font-mono group-hover:text-cyan-400 transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-gray-400 text-sm font-mono leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  {/* Tech stack */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="text-xs font-mono text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 px-2 py-1 rounded"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Project link */}
                  <div className="flex items-center justify-between">
                    <a 
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-mono text-sm transition-colors duration-300 cursor-pointer whitespace-nowrap group-hover:translate-x-1 transform transition-transform duration-300"
                    >
                      <span>{t('explore')}</span>
                      <i className="ri-external-link-line ml-2"></i>
                    </a>
                    <div className="font-mono text-gray-600 text-xs">
                      <span className="text-gray-500">// </span>
                      <span>{t('active')}</span>
                    </div>
                  </div>
                </div>

                {/* Hover effect border */}
                <div className="h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>

          {/* Additional info */}
          <div className="text-center mt-12">
            <div className="inline-flex items-center text-gray-500 font-mono text-xs bg-gray-900/50 border border-gray-700 px-4 py-2 rounded-full">
              <i className="ri-terminal-line mr-2 text-cyan-400"></i>
              <span>{t('contributing')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
